import { setLanguage, setUserInfo } from "@actions/profile";
import { LANGUAGES } from "@enums";
import PropTypes from "prop-types";
import React, { memo, useCallback, useEffect, useState } from "react";
import { getActiveLanguage, getLanguages, setActiveLanguage } from "react-localize-redux";
import { connect } from "react-redux";
import styled from "styled-components";
import { BettoCard } from "../../../components/BettoCard";
import { BettoMaximumDailyDeposit } from "../../../components/BettoMaximumDailyDeposit";
import { BettoRealityCheck } from "../../../components/BettoRealityCheck";
import { BettoSettingsRow } from "../../../components/BettoSettingsRow";
import { BettoToggle } from "../../../components/BettoToggle";
import { BTTO_PROFILE_COLORS } from "../../../shared/elements";

const LanguageFlags = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;

	img {
		width: 32px;
		height: 32px;
		border-radius: 50%;
		cursor: pointer;
		border: 2px solid transparent;
		transition: border-color 0.2s ease;

		&.active {
			border-color: ${BTTO_PROFILE_COLORS.primary};
		}
	}
`;

const FLAGS = {
	tr: "/flags/tr.svg",
	en: "/flags/en.svg",
	es: "/flags/es.svg",
	it: "/flags/it.svg",
	pt: "/flags/pt.svg",
};

export const PreferencesSection = memo(
	({
		translate,
		user,
		setUserInfo,
		currentLanguage,
		supportedLanguages,
		setActiveLanguage,
		setLanguage,
		showAlert,
	}) => {
		const [acceptsSms, setAcceptsSms] = useState(false);
		const [acceptsEmail, setAcceptsEmail] = useState(false);

		useEffect(() => {
			if (user) {
				setAcceptsSms(user.accepts_sms === true);
				setAcceptsEmail(user.accepts_email === true);
			}
		}, [user]);

		const handlePermissionChange = useCallback(
			(field, value) => {
				const nextSms = field === "sms" ? value : acceptsSms;
				const nextEmail = field === "email" ? value : acceptsEmail;

				if (field === "sms") setAcceptsSms(value);
				if (field === "email") setAcceptsEmail(value);

				setUserInfo(user?.username, {
					UserAttributes: [
						{ Name: "accepts_sms", Value: nextSms ? "true" : "false" },
						{ Name: "accepts_email", Value: nextEmail ? "true" : "false" },
					],
				});
			},
			[acceptsSms, acceptsEmail, setUserInfo, user?.username]
		);

		const changeLang = useCallback(
			(code) => {
				localStorage.setItem("lang", code);
				setActiveLanguage(code);
				setLanguage(code);
			},
			[setActiveLanguage, setLanguage]
		);

		const languageLabel =
			LANGUAGES.find((l) => l.code === currentLanguage)?.text || currentLanguage;

		return (
			<>
				<BettoCard title={translate("betto.profile.accountPreferences")}>
					<BettoSettingsRow
						label={translate("Change Language")}
						badge={languageLabel}
						control={
							<LanguageFlags>
								{supportedLanguages.map((lang) => (
									<img
										key={lang.code}
										src={FLAGS[lang.code] || FLAGS.en}
										alt={lang.code}
										className={currentLanguage === lang.code ? "active" : ""}
										onClick={() => changeLang(lang.code)}
									/>
								))}
							</LanguageFlags>
						}
					/>
					{/*
					<BettoSettingsRow
						label={translate("betto.profile.displayMode")}
						control={
							<ThemeToggleRow>
								<ThemeSegment
									type="button"
									$active={theme === "dark"}
									aria-label="Dark mode"
									onClick={() => setTheme("dark")}>
									<HeaderMoonIcon />
								</ThemeSegment>
								<ThemeSegment
									type="button"
									$active={theme === "light"}
									aria-label="Light mode"
									onClick={() => setTheme("light")}>
									<HeaderSunIcon />
								</ThemeSegment>
							</ThemeToggleRow>
						}
					/>
					*/}
				</BettoCard>

				<BettoCard title={translate("betto.profile.emailNotifications")}>
					<BettoSettingsRow
						label={translate("ACCEPTS_EMAIL")}
						control={
							<BettoToggle
								active={acceptsEmail}
								aria-label={translate("ACCEPTS_EMAIL")}
								onChange={(value) => handlePermissionChange("email", value)}
							/>
						}
					/>
					<BettoSettingsRow
						label={translate("ACCEPTS_SMS")}
						control={
							<BettoToggle
								active={acceptsSms}
								aria-label={translate("ACCEPTS_SMS")}
								onChange={(value) => handlePermissionChange("sms", value)}
							/>
						}
					/>
				</BettoCard>

				<BettoCard title={translate("betto.profile.responsibleGaming")}>
					<BettoRealityCheck translate={translate} />
				</BettoCard>

				<BettoCard title={translate("Maximum Daily Deposit Limit")}>
					<BettoMaximumDailyDeposit
						translate={translate}
						showAlert={showAlert}
					/>
				</BettoCard>
			</>
		);
	}
);

PreferencesSection.displayName = "PreferencesSection";

PreferencesSection.propTypes = {
	translate: PropTypes.func.isRequired,
	user: PropTypes.object,
	setUserInfo: PropTypes.func,
	currentLanguage: PropTypes.string,
	supportedLanguages: PropTypes.array,
	setActiveLanguage: PropTypes.func,
	setLanguage: PropTypes.func,
	showAlert: PropTypes.func,
};

const mapStateToProps = (state) => ({
	user: state.profile.user,
	currentLanguage: getActiveLanguage(state.localize).code,
	supportedLanguages: getLanguages(state.localize),
});

const mapDispatchToProps = { setUserInfo, setActiveLanguage, setLanguage };

export default connect(mapStateToProps, mapDispatchToProps)(PreferencesSection);
